import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import useNswagClient from '../../../hooks/api/useNswagClient';
import TitleArea from './components/TitleArea';
import DailyTasks from './widgets/DailyTasks';
import Notifications from './widgets/Notifications';
import OrdersByStatus from './widgets/OrdersByStatus';
import OrdersOverTime from './widgets/OrdersOverTime';
import OrdersByMarket from './widgets/OrdersByMarket';
import SalesByStatus from './widgets/SalesByStatus';
import SalesOverTime from './widgets/SalesOverTime';
import SalesByMarket from './widgets/SalesByMarket';
import RetailersByOnboardingStatus from './widgets/RetailersByOnboardingStatus';
import StaleRetailers from './widgets/StaleRetailers';
import RetailersByValue from './widgets/RetailersByValue';
import TopSellingProductsByValue from './widgets/TopSellingProductsByValue';
import TopSellingProductsByMarket from './widgets/TopSellingProductsByMarket';
import WorstPerformingProducts from './widgets/WorstPerformingProducts';
import DeliveriesByStatus from './widgets/DeliveriesByStatus';
import DeliveriesOverTime from './widgets/DeliveriesOverTime';
import DeliveriesByMarket from './widgets/DeliveriesByMarket';
import DeliveredInFull from './widgets/DeliveredInFull';
import DeliveredOnTime from './widgets/DeliveredOnTime';
import CreditsClaimsByValue from './widgets/CreditsClaimsByValue';
import CreditsClaimsByQuantity from './widgets/CreditsClaimsByQuantity';
import CreditsClaimsByMarket from './widgets/CreditsClaimsByMarket';
import CreditsClaimsByStatus from './widgets/CreditsClaimsByStatus';
import InvoicesByStatus from './widgets/InvoicesByStatus';
import InvoicesByValue from './widgets/InvoicesByValue';
import WidgetWrapper from './components/WidgetWrapper';
import OrdersHeadline from './headlines/OrdersHeadline';
import SalesHeadline from './headlines/SalesHeadline';
import RetailersHeadline from './headlines/RetailersHeadline';
import DeliveriesHeadline from './headlines/DeliveriesHeadline';
import InvoicesHeadline from './headlines/FinanceHeadline';
import {
  RetailerHeaderModel,
  RetailerListModel,
} from '../../../app/services/api/ofdClient';
import { DashboardInvoiceSummaryResponse, DashboardCreditSummaryResponse, DashboardDeliveriesSummaryResponse } from '../../../app/services/api/generated';
import useLogError from '../../../hooks/useLogError';
import { client, Order } from '../../../app/services/api/orderManagementClient';

const DashboardPage = () => {
  const { getRetailersList, getDashboardInvoiceSummary, getDashboardCreditSummary, getDashboardDeliveriesSummary } = useNswagClient();
  const { hasPermissionTo, selectedStore, franchises } = useContext(UserContext);
  const { logError } = useLogError();
  const [retailers, setRetailers] = useState<RetailerHeaderModel[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceSummaryThisMonth, setInvoiceSummaryThisMonth] = useState<DashboardInvoiceSummaryResponse | null>(null);
  const [invoiceSummaryLastMonth, setInvoiceSummaryLastMonth] = useState<DashboardInvoiceSummaryResponse | null>(null);
  const [creditSummaryThisMonth, setCreditSummaryThisMonth] = useState<DashboardCreditSummaryResponse | null>(null);
  const [creditSummaryLastMonth, setCreditSummaryLastMonth] = useState<DashboardCreditSummaryResponse | null>(null);
  const [deliveriesSummaryThisMonth, setDeliveriesSummaryThisMonth] = useState<DashboardDeliveriesSummaryResponse | null>(null);
  const [deliveriesSummaryLastMonth, setDeliveriesSummaryLastMonth] = useState<DashboardDeliveriesSummaryResponse | null>(null);

  const theme = useTheme();

  const { t } = useTranslation('common');

  const [selectedHeadline, setSelectedHeadline] = useState('retailers');

  const getSelectedStyle = (key: string) => {
    return selectedHeadline === key ? {
      cursor: 'cursor',
      borderRadius: '12px 12px 0 0',
      backgroundColor: theme.palette.custom.gray[100],
      border: `1px solid ${theme.palette.custom.gray[300]}`,
      borderBottom: `1px solid ${theme.palette.custom.gray[100]}`,
      marginBottom: '-1px',
      [theme.breakpoints.down('lg')]: {
        borderRadius: '12px',
        marginBottom: '12px',
        borderBottom: `1px solid ${theme.palette.custom.gray[300]}`,
      },
    } : {
      cursor: 'pointer',
      borderRadius: '12px 12px 0 0',
      boxShadow: 'inset 0px -1px 9px -7px',
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.custom.gray[300]}`,
      marginBottom: '-1px',
      [theme.breakpoints.down('lg')]: {
        borderRadius: '12px',
        marginBottom: '12px',
      },
    };
  };

  const loadRetailerLocationData = () => {
    setIsLoading(true);
    getRetailersList({
      franchiseNames: franchises.filter(f => f.franchiseName)
        .map(f => f.franchiseName as string),
    }).then((result: RetailerListModel) => {
      if (result.retailerList) {
        setRetailers(result.retailerList as RetailerHeaderModel[] ?? []);
      }
    }).catch((error) => {
      logError(error);
    }).finally(() => (setIsLoading(false)));
  };

  useEffect(() => {
    // Load initial data or perform any setup here
    loadRetailerLocationData();
    loadDashboardSummaries();
    loadAllOrders();
  }, []);

  const loadAllOrders = () => {
    // Fetch all orders with a large page size
    client.getOrders({
      storeNumber: selectedStore?.storeNumber,
      orderNumber: '',
      item: '',
      orderType: -1,
      status: -1,
      deliveryDateFrom: undefined,
      deliveryDateTo: undefined,
      pageNumber: 1,
      pageSize: 1000000,
      sort: 'OrderNumber',
      direction: 'desc',
    }).then((result) => {
      setOrders(result.data.items);
    }).catch(logError);
  };

  const loadDashboardSummaries = () => {
    const today = new Date();
    const fromDateThisMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
    const toDateThisMonth = today.toISOString();
    const fromDateLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString();
    const toDateLastMonth = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999).toISOString();
    if (hasPermissionTo(['ViewInvoicesByStatus', 'ViewInvoicesByValue'])) {
      getDashboardInvoiceSummary(fromDateThisMonth, toDateThisMonth)
        .then(setInvoiceSummaryThisMonth)
        .catch(logError);
      getDashboardInvoiceSummary(fromDateLastMonth, toDateLastMonth)
        .then(setInvoiceSummaryLastMonth)
        .catch(logError);
    }
    if (hasPermissionTo(['ViewCreditsClaimsByValue', 'ViewCreditsClaimsByQuantity', 'ViewCreditsClaimsByMarket', 'ViewCreditsClaimsByStatus'])) {
      getDashboardCreditSummary(fromDateThisMonth, toDateThisMonth)
        .then(setCreditSummaryThisMonth)
        .catch(logError);
      getDashboardCreditSummary(fromDateLastMonth, toDateLastMonth)
        .then(setCreditSummaryLastMonth)
        .catch(logError);
    }
    if (hasPermissionTo(['ViewDeliveriesByStatus', 'ViewDeliveriesOverTime', 'ViewDeliveriesByMarket', 'ViewDeliveredInFull', 'ViewDeliveredOnTime'])) {
      getDashboardDeliveriesSummary(fromDateThisMonth, toDateThisMonth)
        .then(setDeliveriesSummaryThisMonth)
        .catch(logError);
      getDashboardDeliveriesSummary(fromDateLastMonth, toDateLastMonth)
        .then(setDeliveriesSummaryLastMonth)
        .catch(logError);
    }
  };

  return (
    <Box    >
      <TitleArea ></TitleArea>
      <Box
        sx={{
          padding: '16px',
          backgroundImage: `url(${theme.brandingImages.dashboardImage})`,
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed',
        }}
      >
        <Grid container
          width={undefined}
          marginBottom={'16px'}
          spacing={5}>
          <Grid item
            xs={12}
            lg={6}>
            <DailyTasks storeNumber={selectedStore?.storeNumber}></DailyTasks>
          </Grid>
          <Grid item
            xs={12}
            lg={6}>
            <Notifications storeNumber={selectedStore?.storeNumber} />
          </Grid>
        </Grid>
        <Grid
          spacing={5}
          sx={{
            m: 0,
            ml: '-12px',
          }}
          justifyContent="flex-start"
          container>

          {/* Retailers Tab */}
          {(hasPermissionTo(['ViewRetailersByOnboardingStatus', 'ViewStaleRetailers', 'ViewRetailersByValue'])) && (
            <Grid item
              lg={2}
              key="retailers"
              onClick={() => setSelectedHeadline('retailers')}>
              <Box
                sx={getSelectedStyle('retailers')}
              >
                <RetailersHeadline />
              </Box>
            </Grid>
          )}
          {/* Orders Tab */}
          {(hasPermissionTo(['ViewOrdersStatusPanel', 'ViewOrdersByMarket', 'ViewOrdersOverTime'])) && (
            <Grid item
              lg={2}
              key="orders"
              onClick={() => setSelectedHeadline('orders')}>
              <Box
                sx={getSelectedStyle('orders')}
              >
                <OrdersHeadline orders={orders} />
              </Box>
            </Grid>
          )}
          {/* Sales Tab */}
          {(hasPermissionTo(['ViewSalesByStatus', 'ViewSalesOverTime', 'ViewSalesByMarket', 'ViewTopSellingProductsByValue', 'ViewTopSellingProductsByMarket', 'ViewWorstPerformingProducts'])) && (
            <Grid item
              lg={2}
              key="sales"
              onClick={() => setSelectedHeadline('sales')}>
              <Box
                sx={getSelectedStyle('sales')}
              >
                <SalesHeadline orders={orders} />
              </Box>
            </Grid>
          )}
          {/* Deliveries Tab */}
          {(hasPermissionTo(['ViewDeliveriesByStatus', 'ViewDeliveriesOverTime', 'ViewDeliveriesByMarket', 'ViewDeliveredInFull', 'ViewDeliveredOnTime'])) && (
            <Grid item
              lg={2}
              key="deliveries"
              onClick={() => setSelectedHeadline('deliveries')}>
              <Box
                sx={getSelectedStyle('deliveries')}
              >
                <DeliveriesHeadline
                  deliveriesThisMonth={deliveriesSummaryThisMonth?.deliveries ?? []}
                  deliveriesLastMonth={deliveriesSummaryLastMonth?.deliveries ?? []}
                />
              </Box>
            </Grid>
          )}
          {/* Invoices Tab */}
          {(hasPermissionTo(['ViewInvoicesByStatus', 'ViewInvoicesByValue', 'ViewCreditsClaimsByValue', 'ViewCreditsClaimsByQuantity', 'ViewCreditsClaimsByMarket', 'ViewCreditsClaimsByStatus'])) && (
            <Grid item
              lg={2}
              key="invoices"
              onClick={() => setSelectedHeadline('invoices')}>
              <Box
                sx={getSelectedStyle('invoices')}
              >
                <InvoicesHeadline
                  creditsThisMonth={creditSummaryThisMonth?.credits ?? []}
                  creditsLastMonth={creditSummaryLastMonth?.credits ?? []}
                />
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid container
          spacing={5}
          sx={{
            m: 0, backgroundColor: theme.palette.custom.gray[100],
            padding: '16px', paddingRight: '28px', paddingBottom: '28px',
            borderRadius: ' 0 12px 12px 12px',
            border: `1px solid ${theme.palette.custom.gray[300]}`,
            [theme.breakpoints.down('lg')]: {
              borderRadius: '12px',
              marginBottom: '12px',
              padding: '8px',
              paddingRight: '16px',
              paddingBottom: '16px',
            },
          }}>
          {/* Conditionally render widgets based on selected headline */}
          {selectedHeadline === 'orders' && (
            <>
              {hasPermissionTo(['ViewOrdersStatusPanel']) && (
                <WidgetWrapper size="large"
                  title={t('Orders By Status')}>
                  <OrdersByStatus orders={orders} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewOrdersByMarket']) && (
                <WidgetWrapper size="large"
                  title={t('Orders By Market')}>
                  <OrdersByMarket orders={orders} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewOrdersOverTime']) && (
                <WidgetWrapper size="xlarge"
                  title={t('Orders Over Time')}>
                  <OrdersOverTime orders={orders} />
                </WidgetWrapper>
              )}
            </>
          )}
          {selectedHeadline === 'sales' && (
            <>
              {hasPermissionTo(['ViewSalesByStatus']) && (
                <WidgetWrapper size="medium"
                  title={t('Sales By Status')}>
                  <SalesByStatus orders={orders} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewSalesOverTime']) && (
                <WidgetWrapper size="medium"
                  title={t('Sales Over Time')}>
                  <SalesOverTime orders={orders} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewSalesByMarket']) && (
                <WidgetWrapper size="medium"
                  title={t('Sales By Market')}>
                  <SalesByMarket orders={orders} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewTopSellingProductsByValue']) && (
                <WidgetWrapper size="medium"
                  title={t('Top Selling Products By Value')}>
                  <TopSellingProductsByValue orders={orders} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewTopSellingProductsByMarket']) && (
                <WidgetWrapper size="medium"
                  title={t('Top Selling Products By Market')}>
                  <TopSellingProductsByMarket orders={orders}
                    retailers={retailers} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewWorstPerformingProducts']) && (
                <WidgetWrapper size="medium"
                  title={t('Worst Performing Products')}>
                  <WorstPerformingProducts orders={orders} />
                </WidgetWrapper>
              )}
            </>
          )}
          {selectedHeadline === 'retailers' && (
            <>
              {hasPermissionTo(['ViewRetailersByOnboardingStatus']) && (
                <WidgetWrapper size="large"
                  title={t('Retailers By Onboarding Status')}>
                  <RetailersByOnboardingStatus retailers={retailers} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewStaleRetailers']) && (
                <WidgetWrapper size="large"
                  title={t('Stale Retailers')}>
                  <StaleRetailers />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewRetailersByValue']) && (
                <WidgetWrapper size="xlarge"
                  title={t('Retailers By Value')}>
                  <RetailersByValue />
                </WidgetWrapper>
              )}
              {!hasPermissionTo(['ViewRetailersByOnboardingStatus']) &&
               !hasPermissionTo(['ViewStaleRetailers']) &&
               !hasPermissionTo(['ViewRetailersByValue']) && (
                <Grid item
                  xs={12}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="textMD"
                      color="text.secondary">
                      {'No retailer widgets available. Please contact your administrator for access.'}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </>
          )}
          {selectedHeadline === 'deliveries' && (
            <>
              {hasPermissionTo(['ViewDeliveriesByStatus']) && (
                <WidgetWrapper size="medium"
                  title={t('Deliveries By Status')}>
                  <DeliveriesByStatus deliveries={deliveriesSummaryThisMonth?.deliveries ?? []} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewDeliveriesOverTime']) && (
                <WidgetWrapper size="medium"
                  title={t('Deliveries Over Time')}>
                  <DeliveriesOverTime deliveries={deliveriesSummaryThisMonth?.deliveries ?? []} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewDeliveriesByMarket']) && (
                <WidgetWrapper size="medium"
                  title={t('Deliveries By Market')}>
                  <DeliveriesByMarket deliveries={deliveriesSummaryThisMonth?.deliveries ?? []}
                    retailers={retailers} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewDeliveredInFull']) && (
                <WidgetWrapper size="medium"
                  title={t('Delivered In Full')}>
                  <DeliveredInFull deliveries={deliveriesSummaryThisMonth?.deliveries ?? []} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewDeliveredOnTime']) && (
                <WidgetWrapper size="medium"
                  title={t('Delivered On Time')}>
                  <DeliveredOnTime deliveries={deliveriesSummaryThisMonth?.deliveries ?? []} />
                </WidgetWrapper>
              )}
            </>
          )}
          {selectedHeadline === 'invoices' && (
            <>
              {hasPermissionTo(['ViewInvoicesByStatus']) && (
                <WidgetWrapper size="medium"
                  title={t('Invoices By Status')}>
                  <InvoicesByStatus invoices={invoiceSummaryThisMonth?.invoices ?? []} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewInvoicesByValue']) && (
                <WidgetWrapper size="medium"
                  title={t('Invoices By Value')}>
                  <InvoicesByValue invoices={invoiceSummaryThisMonth?.invoices ?? []} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewCreditsClaimsByValue']) && (
                <WidgetWrapper size="medium"
                  title={t('Credits Claims By Value')}>
                  <CreditsClaimsByValue credits={creditSummaryThisMonth?.credits ?? []} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewCreditsClaimsByQuantity']) && (
                <WidgetWrapper size="medium"
                  title={t('Credits Claims By Quantity')}>
                  <CreditsClaimsByQuantity credits={creditSummaryThisMonth?.credits ?? []} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewCreditsClaimsByMarket']) && (
                <WidgetWrapper size="medium"
                  title={t('Credits Claims By Market')}>
                  <CreditsClaimsByMarket
                    credits={creditSummaryThisMonth?.credits ?? []}
                    retailers={retailers} />
                </WidgetWrapper>
              )}
              {hasPermissionTo(['ViewCreditsClaimsByStatus']) && (
                <WidgetWrapper size="medium"
                  title={t('Credits Claims By Status')}>
                  <CreditsClaimsByStatus
                    credits={creditSummaryThisMonth?.credits ?? []} />
                </WidgetWrapper>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default DashboardPage;
